import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFPatientInfo from '../base/PDFPatientInfo';
import { PDFMetadataFooterData } from '../base/PDFMetadataFooter';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles } from '../styles/typography';
import { createTwoColumnLayout } from '../styles/layout';
import { PDFSurveyRecordRenderer } from '../utils/contentRenderer';
import { formatDateForPDF, formatPatientName } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { PDFFieldRenderer } from '../utils/PDFFieldRenderer';
import { diagnosisLabelsFromRecords, withCatalogueNames } from '../../../utils/icd11Display';

// Helper function for researcher names
const formatResearcherName = (researcher: IResearcher): string => {
  return `${researcher.name} ${researcher.surnames}`.trim();
};

/**
 * Submission PDF Document Component
 * 
 * Handles patient survey submissions and form responses.
 * Displays survey sections with their corresponding field responses.
 */

interface IPatient {
  personalData: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
    insurance?: string | number;
  };
}

interface IResearcher {
  uuid: string;
  name: string;
  surnames: string;
}

interface ISurveyField {
  id: string | number;
  uuid?: string;
  name: string;
  label: string;
  type: string;
  order: number;
  options?: string[] | any[];
}

interface ISurveyFieldGroupSchema {
  id: number;
  uuid?: string;
  name: string;
  fields: ISurveyField[];
}

interface ISurveySection {
  id?: number;
  uuid: string;
  name: string;
  order: number;
  fields: ISurveyField[];
  fieldGroups?: ISurveyFieldGroupSchema[];
}

interface ISurvey {
  uuid: string;
  name: string;
  sections: { [key: string]: ISurveySection } | ISurveySection[];
}

interface ISurveyRecord {
  id: string | number;
  value: any;
  surveyField: ISurveyField;
  surveySection?: any;
}

interface ISubmissionFieldGroupInstance {
  instanceNumber: number;
  groupRecordId?: number;
  surveyRecords: ISurveyRecord[];
}

interface ISubmissionFieldGroup {
  fieldGroupId: number;
  fieldGroupUuid?: string;
  instances: ISubmissionFieldGroupInstance[];
}

interface ISubmission {
  id: string | number;
  createdAt: string | Date;
  updatedAt: string | Date;
  uuidResearcher?: string;
  researcher?: IResearcher | string;
  records?: ISurveyRecord[];
  surveyRecords?: ISurveyRecord[];
  // SKIN-128: grouped survey records are returned separately from the flat
  // list (the API filters them out of surveyRecords); render them too.
  fieldGroups?: ISubmissionFieldGroup[];
}

interface SubmissionPDFDocumentProps {
  submission: ISubmission;
  currentSurvey: ISurvey;
  patient: IPatient;
  prescribingDoctor?: IResearcher;
  config: PDFConfig;
  nameSurvey: string;
  locale?: string;
  uuidInvestigation: string; // Investigation UUID (for reference/tracking)
  signatureBase64?: string | null; // Base64-encoded signature image from parent component (if available)
  /**
   * SKIN-612 — WHO names for the ICD-11 codes no pick of the submission names, fetched by the
   * caller (`useSubmissionCatalogueNames`): a PDF is rendered from props and cannot fetch.
   */
  catalogueNames?: ReadonlyMap<string, string>;
}

export const SubmissionPDFDocument: React.FC<SubmissionPDFDocumentProps> = ({
  submission,
  currentSurvey,
  patient,
  prescribingDoctor,
  config,
  nameSurvey,
  locale = 'fr',
  uuidInvestigation,
  signatureBase64,
  catalogueNames,
}) => {
  const t = createT(locale);

  // Group records by section
  const recordsBySection = React.useMemo(() => {
    const records = submission.records || submission.surveyRecords || [];
    if (!records.length) return {};
    
    const grouped: { [sectionUuid: string]: ISurveyRecord[] } = {};
    const sections = Array.isArray(currentSurvey.sections) 
      ? currentSurvey.sections 
      : Object.values(currentSurvey.sections);
    
    records.forEach(record => {
      // Find which section this field belongs to
      const section = sections.find(s => 
        s.fields.some(f => f.id === record.surveyField.id || f.id === (record.surveyField as any).uuid)
      );
      
      if (section) {
        if (!grouped[section.uuid]) {
          grouped[section.uuid] = [];
        }
        grouped[section.uuid].push(record);
      }
    });
    
    return grouped;
  }, [submission, currentSurvey.sections]);

  // SKIN-612 — names for the bare codes `lesion_diagnoses` stores: from every ICD-11 pick of
  // this submission, flat or grouped, then from the WHO catalogue for the codes none names.
  const diagnosisLabels = React.useMemo(() => withCatalogueNames(diagnosisLabelsFromRecords([
    ...(submission.surveyRecords || []),
    ...(submission.fieldGroups || []).flatMap(fg => (fg.instances || []).flatMap(instance => instance.surveyRecords || [])),
  ]), catalogueNames ?? new Map()), [submission, catalogueNames]);

  const renderSurveySection = (section: ISurveySection, index: number) => {
    const sectionRecords = recordsBySection[section.uuid] || [];
    const isFirstSection = index === 0;
    const shouldHideTitle = isFirstSection && section.name === nameSurvey;
    
    return (
      <View key={section.uuid} style={[commonPDFStyles.section]}>
        {!shouldHideTitle && (
          <Text style={typographyStyles.h4}>
            {section.name}
          </Text>
        )}
        <View style={commonPDFStyles.sectionContent}>
          {section.fields
            .sort((a, b) => a.order - b.order)
            .map(field => {
              const record = sectionRecords.find(r => r.surveyField.id === field.id);
              return renderSurveyField(field, record);
            })}
          {renderSectionFieldGroups(section)}
        </View>
      </View>
    );
  };

  // SKIN-128: render field-group records for a section. The API returns grouped
  // records under submission.fieldGroups (keyed by fieldGroupId) rather than in
  // the flat surveyRecords list, so without this every field placed inside a
  // SurveyFieldGroup — repeating lesion groups and non-repeating groups alike —
  // was omitted from the PDF. Renders the same grouped values the on-screen
  // submission view shows (show_records_section renderFieldGroups): only groups
  // this submission actually populated, each instance's fields via renderFieldValue.
  const renderSectionFieldGroups = (section: ISurveySection) => {
    const schemaGroups = section.fieldGroups || [];
    if (!schemaGroups.length) return null;
    const submissionGroups = submission.fieldGroups || [];

    return schemaGroups.map(groupSchema => {
      const submissionGroup = submissionGroups.find(fg => fg.fieldGroupId === groupSchema.id);
      // Group not present in this submission — omit it, so the PDF shows the same
      // set of groups the on-screen view does. We iterate the schema groups (PDF
      // order follows the survey definition) and skip any the submission didn't
      // populate; the on-screen view reaches the same result by iterating the
      // submission's groups instead.
      if (!submissionGroup) return null;
      const instances = submissionGroup.instances || [];
      const sortedFields = [...(groupSchema.fields || [])].sort((a, b) => a.order - b.order);

      return (
        <View key={groupSchema.uuid || groupSchema.id} style={[commonPDFStyles.marginBottom]}>
          <Text style={typographyStyles.subtitle2}>{groupSchema.name}</Text>
          {instances.length === 0 ? (
            <Text style={typographyStyles.body2}>-</Text>
          ) : (
            [...instances]
              .sort((a, b) => a.instanceNumber - b.instanceNumber)
              .map((instance, idx) => (
                <View key={instance.groupRecordId ?? idx} style={{ paddingLeft: 8, marginBottom: 4 }}>
                  {instances.length > 1 && (
                    <Text style={typographyStyles.body2}>#{idx + 1}</Text>
                  )}
                  {sortedFields.map(field => {
                    const record = instance.surveyRecords.find(r => r.surveyField.id === field.id);
                    return (
                      <View key={field.id} style={[commonPDFStyles.marginBottom]}>
                        <Text style={typographyStyles.subtitle2}>{field.name}:</Text>
                        <View style={{ paddingLeft: 8 }}>
                          {renderFieldValue(field, record)}
                        </View>
                      </View>
                    );
                  })}
                </View>
              ))
          )}
        </View>
      );
    });
  };

  const renderSurveyField = (field: ISurveyField, record?: ISurveyRecord) => {
    const sectionCount = Array.isArray(currentSurvey.sections) 
      ? currentSurvey.sections.length 
      : Object.keys(currentSurvey.sections).length;
    const showFieldName = currentSurvey.sections && sectionCount > 1;
    
    return (
      <View key={field.id} style={[commonPDFStyles.marginBottom]}>
        {showFieldName && (
          <Text style={typographyStyles.subtitle2}>
            {field.name}:
          </Text>
        )}
        
        <View style={{ paddingLeft: 8 }}>
          {renderFieldValue(field, record)}
        </View>
      </View>
    );
  };

  const renderFieldValue = (field: ISurveyField, record?: ISurveyRecord) => {
    // Use unified field renderer
    return (
      <PDFFieldRenderer
        field={field}
        value={record?.value}
        locale={locale}
        diagnosisLabels={diagnosisLabels}
      />
    );
  };

  const renderSubmissionInfo = () => {
    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('hospital.submission.info')}
        </Text>
        
        <View style={commonPDFStyles.sectionContent}>
          <Text style={typographyStyles.body2}>
            <Text style={typographyStyles.button}>{t('pages.request.pdf.created_at')}</Text>: {formatDateForPDF(submission.createdAt, locale)}
          </Text>
          
          {submission.updatedAt !== submission.createdAt && (
            <Text style={typographyStyles.body2}>
              <Text style={typographyStyles.button}>{t('pages.request.pdf.updated_at')}</Text>: {formatDateForPDF(submission.updatedAt, locale)}
            </Text>
          )}
        </View>
      </View>
    );
  };

  const renderFooter = () => {
    if (!prescribingDoctor) return null;

    return (
      <View style={[commonPDFStyles.signature, commonPDFStyles.pageBreakAvoid]}>
        {/* Signature placeholder (label + name + title) */}
        <Text style={commonPDFStyles.signatureName}>
          {t('general.signature')}
        </Text>
        <Text style={commonPDFStyles.signatureName}>
          {formatResearcherName(prescribingDoctor)}
        </Text>
        <Text style={commonPDFStyles.signatureTitle}>
          {t('hospital.doctor.prescribing_doctor')}
        </Text>

        {/* Signature image below placeholder */}
        {signatureBase64 && (
          <View style={{ marginTop: 10, alignItems: 'center' }}>
            <Image 
              src={signatureBase64} 
              style={{ 
                width: 150, 
                height: 75, 
                objectFit: 'contain',
              }} 
            />
          </View>
        )}
      </View>
    );
  };

  // Sort sections by order - handle both array and object structures
  const sortedSections = (Array.isArray(currentSurvey.sections) 
    ? currentSurvey.sections 
    : Object.values(currentSurvey.sections)
  ).sort((a, b) => a.order - b.order);

  return (
    <PDFDocument
      title={nameSurvey}
      patientInfo={<PDFPatientInfo patient={patient} locale={locale} />}
      config={config}
      locale={locale}
      footer={renderFooter()}
      metadataFooter={{
        patient: patient as PDFMetadataFooterData['patient'],
        documentReference: String(submission.id),
        documentDate: submission.createdAt,
        validatedBy: prescribingDoctor
          ? {
              name: formatResearcherName(prescribingDoctor),
              title: t('hospital.doctor.prescribing_doctor'),
            }
          : undefined,
        locale,
        hospitalName: config.hospitalName,
        city: config.city,
      }}
    >
      {renderSubmissionInfo()}
      
      {sortedSections.map((section, index) => renderSurveySection(section, index))}
    </PDFDocument>
  );
};

export default SubmissionPDFDocument;
